/**
 * @Author Heriberto Monterrubio <heri185403@gmail.com>
 * @Date 2020-Oct-05
 *
 **/

import {Procedures} from "../api";
import {procedureCountStatusRequest, procedureListRequest, procedureSubmitNextRequest} from "../api/models";
import Constants from "../core/constants";
import {procedureCreateRequest} from "../api/models";
import procedureTaskStepsRequest from "@/api/models/procedure/task-steps.request";
import {toFormData} from "@/core/utils";
import router from "@/config/router";
import * as Storage from '@/core/storage.js'

const proceduresModule =  {
    namespaced: true,
    state: {
        homeList: null,
        filterStatus: Constants.FILTER_STATUS_ALL,
        pageLimit: 10,

        proceduresCounter: null,
        totalCounter: null,
        lastRequestData: {},

        procedureDataForm: null,
        taskSteps: null,
        executingProcedure: false,

        submitButtonContent: null,

        apiAjaxContent: null,
        apiAjaxFieldId: 0,

        formErrors: null
    },
    getters: {
        hasPagination: state  => {
            return state.homeList != null
        },

        paginator: state => {
            return state.homeList.paginator
        },

        procedures: state => {
            return state.homeList.data
        },

        procedureDataForm: state => {
            return state.procedureDataForm
        },

        form: state => {
            if (typeof state.procedureDataForm.form === 'object'){
                return Object.values(state.procedureDataForm.form)
            }

            return state.procedureDataForm.form
        },

        formErrors: state => {
            return state.procedureDataForm.error.details
        },

        procedureData: state => {
            return state.procedureDataForm.process_data
        },

        procedureMeta: state => {
            return state.procedureDataForm.meta
        },

        formNonContinue: state => {
            return state.procedureDataForm.status === 'sincontinuacion'
        },

        getSubmitButtonContent: state => {
            return state.submitButtonContent
        },

        isPostNextRequest: state => {
            return state.procedureDataForm.method.toUpperCase() === 'POST'
        }
    }
    ,
    actions: {
        async getProceduresByUser({commit, dispatch}, request = procedureListRequest()) {
            dispatch('alerts/showLoading', 'Consultando listado de trámites', { root: true })
            commit('setCurrentFilterStatus', request.status, request.limit)

            await dispatch('account/checkAuthExpiration', null, { root: true })

            Procedures.getListByUser(request)
                .then(data => {
                    dispatch('alerts/hideLoading', null, { root: true })
                    commit('showList', data)
                })
                // .catch(error => {
                //     dispatch('alerts/error', error.message,  { root: true })
                // })
        },

        async getProceduresCount({commit, dispatch}, request = procedureCountStatusRequest()){
            await dispatch('account/checkAuthExpiration', null, { root: true })

            Procedures.getStatusCount(request).then( data => {
                commit('setProceduresCount', data)
            })
        },

        async createProcedure({commit, dispatch}, request = procedureCreateRequest()){
            commit('clearLastProcedureForm')
            dispatch('alerts/showLoading', 'Iniciando nuevo trámite', { root: true })

            await dispatch('account/checkAuthExpiration', null, { root: true })

            Procedures.getStartNew(request.processId, request.invited, request.sessionUser)
                .then(data => {
                    dispatch('alerts/hideLoading', null, { root: true })
                    commit('startProcedureSuccess', data)

                    dispatch('showFormErrorIfExist', data)
                })
                .catch(error => {
                    dispatch('alerts/error', error.message,  { root: true })
                })
        },

        async getProcedureTaskSteps({commit, dispatch}, request = procedureTaskStepsRequest()) {
            dispatch('alerts/showLoading', 'Obteniendo información de la tarea', { root: true })

            await dispatch('account/checkAuthExpiration', null, { root: true })

            Procedures.getTaskSteps(request.taskId, request.stageId)
                .then(data => {
                    dispatch('alerts/hideLoading', null, { root: true })
                    commit('setTaskStages', data)
                })
                .catch(error => {
                    dispatch('alerts/error', error.message,  { root: true })
                })
        },

        async submitProcedureNextStep({dispatch, commit}, request = procedureSubmitNextRequest()) {
            dispatch('alerts/showLoading', 'Enviando información del formulario', {root: true})
            
            await dispatch('account/checkAuthExpiration', null, {root: true})

            Procedures.postExecuteForm(request.url, toFormData(request.data))
                .then(data => {
                    dispatch('alerts/hideLoading', null, { root: true })

                    if (data.error.details !== undefined){
                        commit('setFormErrors', data.error.details)
                    }

                    if (data.next_url != null){
                        let nextUrl = data.next_url.replace(process.env.VUE_APP_API_BASE_URL, '')
                        request.url = nextUrl

                        dispatch('procedures/getProcedureExecutedStep', request, { root: true })
                    }else{
                        commit('submitProcedureNextSuccess', data)

                        if (request.lastAction === 'create'){
                            console.log('antes de error');
                            router.push({name: 'procedure_execute', params: {processId: request.processId}}).catch()
                        }
                    }

                    dispatch('showFormErrorIfExist', data)

                })
                .catch(error => {
                    dispatch('alerts/error', error.message,  { root: true })
                })
        },

        async getProcedureExecutedStep({commit, dispatch}, request = procedureSubmitNextRequest()){
            dispatch('alerts/showLoading', 'Obteniendo información del formulario anterior', {root: true})
            await dispatch('account/checkAuthExpiration', null, {root: true})
            commit('lastRequest', request) // TEST

            commit('clearLastProcedureForm')

            Procedures.getExecuteForm(request.url)
                .then(data => {
                    dispatch('alerts/hideLoading', null, { root: true })
                    commit('submitProcedureNextSuccess', data)

                    Storage.setItem(Constants.KEY_LAST_FORM_URL, request.url)
                    Storage.setItem(Constants.KEY_LAST_PROCESS_EXECUTED, request.processId.toString())
                    if (request.lastAction === 'create'){
                        router.push({name: 'procedure_execute', params: {processId: request.processId}}).catch(error=>{
                            console.log('error: ', error);
                        })
                    }

                    dispatch('showFormErrorIfExist', data)
                })
                .catch(error => {
                    dispatch('alerts/error', error.message,  { root: true })
                })
        },

        async continueProcedure({commit, dispatch}, stageId){
            commit('clearLastProcedureForm')
            dispatch('alerts/showLoading', 'Esperando la información para continuar con el trámite', { root: true })

            await dispatch('account/checkAuthExpiration', null, { root: true })

            Procedures.getContinueStage(stageId)
                .then(data => {
                    dispatch('alerts/hideLoading', null, { root: true })
                    commit('startProcedureSuccess', data)

                    dispatch('showFormErrorIfExist', data)
                })
                .catch(error => {
                    dispatch('alerts/error', error.message,  { root: true })
                })
        },

        async viewProcedure({commit, dispatch}, stageId){
            commit('clearLastProcedureForm')
            dispatch('alerts/showLoading', 'Esperando la información para ver el trámite', { root: true })
            await dispatch('account/checkAuthExpiration', null, { root: true })

            Procedures.getViewStage(stageId)
                .then(data => {
                    commit('startProcedureSuccess', data)

                    dispatch('showFormErrorIfExist', data)
                })
                .catch(error => {
                    dispatch('alerts/error', error.message,  { root: true })
                })
        },

        async getSubmitButtonRequest({commit, dispatch}, request){
            dispatch('alerts/showLoading', 'Abriendo el contenido', { root: true })
            await dispatch('account/checkAuthExpiration', null, { root: true })

            Procedures.getSubmitRequest(request.stage_id, request.field_id)
                .then(data => {
                    dispatch('alerts/hideLoading', null, { root: true })
                    commit('setSubmitButtonContent', data)
                })
                .catch(error => {
                    dispatch('alerts/error', error.message,  { root: true })
                })
        },

        clearProcedures({commit}) {
            commit('clearProcedures')
        },

        clearProcedureForm({commit}) {
            commit('clearLastProcedureForm')
        },

        showFormErrorIfExist({dispatch}, data){
            if (data.error !== undefined && data.error.details.length > 1){
                dispatch('alerts/error', data.error.details[0], { root: true })
            }
        },

        async getApiAjaxContent({commit, dispatch}, request){
            dispatch('alerts/showLoading', 'Obteniendo la información solicitada', { root: true })
            await dispatch('account/checkAuthExpiration', null, { root: true })

            Procedures.getApiAjaxResult(request)
                .then(data => {
                    dispatch('alerts/hideLoading', null, { root: true })
                    commit('setApiAjaxResult', {fieldId: request.fieldId, result: data})
                })
                .catch(error => {
                    commit('setApiAjaxResult', {fieldId: null, result: []})
                    dispatch('alerts/error', error.message,  { root: true })
                })
        }
    },
    mutations: {
        showList(state, data){
            state.hasPagination = true
            state.homeList = data
        },
        clearProcedures(state){
            state.homeList = null
        },
        setCurrentFilterStatus(state, filterStatus, pageLimit){
            state.filterStatus = filterStatus
            state.pageLimit = pageLimit
        },
        setProceduresCount(state, counter){
            state.totalCounter = counter.canceled + counter.complete + counter.in_process + counter.pending
            state.proceduresCounter = counter
        },
        startProcedureSuccess(state, dataForm){
            state.procedureDataForm = dataForm
        },
        lastRequest(state, request){
            state.lastRequestData = request
        },
        setTaskStages(state, data){
            state.taskSteps = data.pasos.items
        },
        clearLastProcedureForm(state){
            state.procedureDataForm = null
            state.taskSteps = null
            state.executingProcedure = true
        },
        submitProcedureNextSuccess(state, dataForm){
            //state.executingProcedure = false
            state.procedureDataForm = dataForm
        },
        setSubmitButtonContent(state, data){
            state.submitButtonContent = data
        },

        setApiAjaxResult(state, data){
            state.apiAjaxContent = data.result
            state.apiAjaxFieldId = data.fieldId
        },

        setFormErrors(state, errors){
            state.formErrors = errors
        }
    },
}

export default proceduresModule